import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  ExternalLink,
  Globe,
  Linkedin,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  ShieldOff,
  Sparkles,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ConnectButton } from '@/components/profile/ConnectButton';
import { OnlineIndicator } from '@/components/profile/OnlineIndicator';
import { useProfile } from '@/lib/hooks/useProfiles';
import { useAuth } from '@/lib/hooks/useAuth';
import { useProfileActivity } from '@/lib/hooks/useProfileActivity';
import { getMyVerificationRequest } from '@/lib/api/profiles';
import { listConnections } from '@/lib/api/connections';
import { PostCard } from '@/components/feed/PostCard';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils/cn';
import type { Database } from '@/lib/supabase/types';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

const TABS = ['About', 'Activity', 'Connections'] as const;
type Tab = (typeof TABS)[number];

function isVerifiedTier(tier: string | null): boolean {
  return tier === 'verified' || tier === 'admin_verified';
}

export function Profile() {
  const { handle } = useParams<{ handle: string }>();
  const { data: profile, isLoading, isError, error } = useProfile(handle);
  const { profile: myProfile } = useAuth();
  const [tab, setTab] = useState<Tab>('About');

  const isOwnProfile = !!myProfile && !!profile && myProfile.id === profile.id;

  const { data: myVerificationRequest } = useQuery({
    queryKey: ['my-verification-request'],
    queryFn: getMyVerificationRequest,
    enabled: isOwnProfile,
    staleTime: 30_000,
  });

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (isError) {
    return (
      <Shell>
        <div className="container py-20">
          <p role="alert" className="text-sm text-destructive">
            Couldn't load this profile
            {error instanceof Error ? `: ${error.message}` : '.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (!profile) {
    return (
      <Shell>
        <div className="container py-20 text-center">
          <h1 className="font-display text-3xl text-navy">Profile not found</h1>
          <p className="mt-3 text-sm text-muted">
            We couldn't find a member with the handle{' '}
            <code className="rounded bg-navy/5 px-1.5 py-0.5 text-navy">
              @{handle}
            </code>
            .
          </p>
          <Button asChild variant="gold" className="mt-6">
            <Link to="/directory">Back to directory</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  const location = [profile.current_city, profile.current_country]
    .filter(Boolean)
    .join(', ');
  const origin = [profile.origin_city, profile.origin_country]
    .filter(Boolean)
    .join(', ');
  const headlineOrRole = profile.headline ?? profile.role_title;
  const verified = isVerifiedTier(profile.verification);

  return (
    <Shell>
      <div className="relative h-48 bg-gradient-to-br from-navy-darker via-navy-deep to-navy sm:h-64">
        {profile.cover_url && (
          <img
            src={profile.cover_url}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-80"
          />
        )}
      </div>

      <div className="container relative -mt-16 pb-16 sm:-mt-20">
        <div className="flex flex-col gap-6 rounded-2xl border border-border/70 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:gap-6">
            <Avatar
              name={profile.display_name}
              src={profile.avatar_url}
              size="xl"
              className="border-4 border-white bg-cream shadow-md"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-3xl text-navy sm:text-4xl">
                  {profile.display_name}
                </h1>
                {profile.status === 'seeded' && (
                  <Badge variant="seeded">Intro only</Badge>
                )}
                {(profile.status === 'claimed' || profile.status === 'active') && !verified && (
                  <Badge variant="default">
                    <ShieldOff className="h-3 w-3" aria-hidden="true" />
                    Unverified
                  </Badge>
                )}
                {verified && (
                  <Badge variant="verified">
                    <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                    Verified
                  </Badge>
                )}
              </div>
              {profile.handle && (
                <p className="mt-1 text-sm text-muted">@{profile.handle}</p>
              )}
              {myProfile && myProfile.id !== profile.id && (
                <OnlineIndicator
                  profileId={profile.id}
                  lastSeenAt={profile.last_seen_at}
                  mode="dot-with-text"
                  className="mt-1"
                />
              )}
              {headlineOrRole && (
                <p className="mt-3 text-base text-ink/80">
                  {headlineOrRole}
                  {profile.company ? ` · ${profile.company}` : ''}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted">
                {location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                    {location}
                  </span>
                )}
                {profile.industry && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
                    {profile.industry}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {myProfile && myProfile.id !== profile.id && (
                <ConnectButton targetProfileId={profile.id} />
              )}
              {profile.linkedin_url && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Linkedin className="h-4 w-4" aria-hidden="true" />
                    LinkedIn
                  </a>
                </Button>
              )}
              {profile.website_url && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Globe className="h-4 w-4" aria-hidden="true" />
                    Website
                  </a>
                </Button>
              )}
              {profile.contact_email && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a href={`mailto:${profile.contact_email}`}>
                    <Mail className="h-4 w-4" aria-hidden="true" />
                    Email
                  </a>
                </Button>
              )}
              {profile.telegram_handle && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a
                    href={`tg://resolve?domain=${profile.telegram_handle}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Telegram
                  </a>
                </Button>
              )}
              {profile.whatsapp && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a
                    href={`https://wa.me/${profile.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    WhatsApp
                  </a>
                </Button>
              )}
              {profile.phone && (
                <Button asChild variant="ghost-dark" size="sm">
                  <a href={`tel:${profile.phone}`}>
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    Call
                  </a>
                </Button>
              )}
            </div>
          </div>

          {profile.status === 'seeded' && (
            <SeededBanner profile={profile} />
          )}
          {isOwnProfile && !isVerifiedTier(profile.verification) && (
            <VerifyNudge requestStatus={myVerificationRequest?.status ?? null} />
          )}
        </div>

        <div
          role="tablist"
          aria-label="Profile sections"
          className="mt-8 flex flex-wrap gap-1 border-b border-border/70"
        >
          {TABS.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                '-mb-px border-b-2 px-4 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40',
                tab === t
                  ? 'border-gold text-navy'
                  : 'border-transparent text-muted hover:text-navy',
              )}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'About' && <AboutTab profile={profile} origin={origin} />}
          {tab === 'Activity' && <ActivityTab profile={profile} />}
          {tab === 'Connections' && (
            <ProfileConnectionsTab profileId={profile.id} displayName={profile.display_name} />
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <AppHeader />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function VerifyNudge({ requestStatus }: { requestStatus: 'pending' | 'approved' | 'rejected' | null }) {
  if (requestStatus === 'pending') {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3">
        <ShieldCheck className="h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" />
        <p className="text-sm text-navy">Verification request pending — we'll review and notify you shortly.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-border/70 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <p className="text-sm text-navy">
          {requestStatus === 'rejected'
            ? 'Your last verification request was not approved.'
            : 'Get a Verified badge to show your identity has been confirmed.'}
        </p>
      </div>
      <Button asChild variant="gold" size="sm" className="shrink-0">
        <Link to="/me/verify">
          {requestStatus === 'rejected' ? 'Try again' : 'Request verification'}
        </Link>
      </Button>
    </div>
  );
}

function SeededBanner({ profile }: { profile: ProfileRow }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gold/40 bg-gold/10 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold-dark">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <p className="font-display text-sm text-navy">
            Introduced via Facebook — not yet claimed
          </p>
          <p className="mt-0.5 text-xs text-ink/70">
            This profile was imported from the 2013+ CPN Facebook intros. The
            member hasn't signed up yet.
          </p>
          {profile.legacy_post_url && (
            <a
              href={profile.legacy_post_url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gold-dark hover:underline"
            >
              View original intro
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      <Button asChild variant="gold" size="sm">
        <Link to={`/profile/${profile.handle}/claim`}>
          This is me — request verification
        </Link>
      </Button>
    </div>
  );
}

function AboutTab({
  profile,
  origin,
}: {
  profile: ProfileRow;
  origin: string;
}) {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <article className="rounded-2xl border border-border/70 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg text-navy">About</h2>
        {profile.bio ? (
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/80">
            {profile.bio}
          </p>
        ) : (
          <p className="mt-3 text-sm text-muted">
            No bio yet. When {profile.display_name} claims this profile, they'll
            add one.
          </p>
        )}
      </article>

      <aside className="flex flex-col gap-4">
        <article className="rounded-2xl border border-border/70 bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg text-navy">Details</h2>
          <dl className="mt-4 space-y-3 text-sm">
            {profile.industry && (
              <Detail
                icon={<Briefcase className="h-3.5 w-3.5" />}
                label="Industry"
                value={profile.industry}
              />
            )}
            {profile.company && (
              <Detail
                icon={<Building2 className="h-3.5 w-3.5" />}
                label="Company"
                value={profile.company}
              />
            )}
            {origin && (
              <Detail
                icon={<MapPin className="h-3.5 w-3.5" />}
                label="From"
                value={origin}
              />
            )}
            {profile.contact_email && (
              <Detail
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Email"
                value={profile.contact_email}
                href={`mailto:${profile.contact_email}`}
              />
            )}
            {profile.phone && (
              <Detail
                icon={<Phone className="h-3.5 w-3.5" />}
                label="Phone"
                value={profile.phone}
                href={`tel:${profile.phone}`}
              />
            )}
            {profile.telegram_handle && (
              <Detail
                icon={<Send className="h-3.5 w-3.5" />}
                label="Telegram"
                value={`@${profile.telegram_handle}`}
                href={`tg://resolve?domain=${profile.telegram_handle}`}
              />
            )}
            {profile.whatsapp && (
              <Detail
                icon={<MessageCircle className="h-3.5 w-3.5" />}
                label="WhatsApp"
                value={profile.whatsapp}
                href={`https://wa.me/${profile.whatsapp.replace(/\D/g, '')}`}
              />
            )}
          </dl>
        </article>

        {(profile.languages.length > 0 ||
          profile.skills.length > 0 ||
          profile.interests.length > 0) && (
          <article className="rounded-2xl border border-border/70 bg-white p-6 shadow-sm">
            {profile.languages.length > 0 && (
              <TagGroup label="Languages" items={profile.languages} />
            )}
            {profile.skills.length > 0 && (
              <TagGroup
                label="Skills"
                items={profile.skills}
                className="mt-5"
              />
            )}
            {profile.interests.length > 0 && (
              <TagGroup
                label="Interests"
                items={profile.interests}
                className="mt-5"
              />
            )}
          </article>
        )}
      </aside>
    </div>
  );
}

function ActivityTab({ profile }: { profile: ProfileRow }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useProfileActivity(profile.id);
  const author = {
    id: profile.id,
    display_name: profile.display_name,
    handle: profile.handle,
    avatar_url: profile.avatar_url,
    headline: profile.headline,
    last_seen_at: profile.last_seen_at,
  };

  const items = data?.pages.flat() ?? [];

  if (items.length === 0 && !isFetchingNextPage) {
    return (
      <EmptyTab
        title="No posts yet"
        body={
          profile.status === 'seeded'
            ? `When ${profile.display_name} claims this profile, their posts will appear here.`
            : 'This member hasn\'t posted yet.'
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map(({ post, reactions, commentCount }) => (
        <PostCard key={post.id} post={post} author={author} reactions={reactions} commentCount={commentCount} />
      ))}
      {hasNextPage && (
        <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

function Detail({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted">
        <span aria-hidden="true">{icon}</span>
        {label}
      </dt>
      <dd className="mt-0.5 text-ink">
        {href ? (
          <a
            href={href}
            target={href.startsWith('mailto:') || href.startsWith('tel:') ? undefined : '_blank'}
            rel="noreferrer noopener"
            className="hover:underline"
          >
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function TagGroup({
  label,
  items,
  className,
}: {
  label: string;
  items: string[];
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-medium uppercase tracking-widest text-muted">
        {label}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((it) => (
          <Badge key={it} variant="outline">
            {it}
          </Badge>
        ))}
      </div>
    </div>
  );
}

type ProfileSnippet = {
  id: string;
  display_name: string;
  handle: string | null;
  avatar_url: string | null;
  headline: string | null;
};

async function fetchConnectionProfiles(profileId: string): Promise<ProfileSnippet[]> {
  const connections = await listConnections(profileId);
  const otherIds = connections.map((c) =>
    c.requester_id === profileId ? c.addressee_id : c.requester_id,
  );
  if (!otherIds.length) return [];
  const { data } = await supabase
    .from('profiles')
    .select('id, display_name, handle, avatar_url, headline')
    .in('id', otherIds)
    .throwOnError();
  return (data ?? []) as ProfileSnippet[];
}

function ProfileConnectionsTab({
  profileId,
  displayName,
}: {
  profileId: string;
  displayName: string;
}) {
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['profile-connections', profileId],
    queryFn: () => fetchConnectionProfiles(profileId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl border border-border/70 bg-white p-4 shadow-sm">
            <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <EmptyTab
        title="No connections yet"
        body={`${displayName} hasn't connected with other members yet.`}
      />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {profiles.map((p) => (
        <Link
          key={p.id}
          to={p.handle ? `/profile/${p.handle}` : '#'}
          className="flex items-center gap-3 rounded-2xl border border-border/70 bg-white p-4 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
        >
          <Avatar src={p.avatar_url} name={p.display_name} size="md" />
          <div className="min-w-0">
            <p className="truncate font-display text-sm text-navy">{p.display_name}</p>
            {p.headline && (
              <p className="truncate text-xs text-muted">{p.headline}</p>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

function EmptyTab({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
      <p className="font-display text-lg text-navy">{title}</p>
      <p className="mt-2 text-sm text-muted">{body}</p>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <Shell>
      <Skeleton className="h-48 w-full rounded-none sm:h-64" />
      <div className="container -mt-16 pb-16 sm:-mt-20">
        <div className="rounded-2xl border border-border/70 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:gap-6">
            <Skeleton className="h-24 w-24 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-2/3 max-w-sm" />
              <Skeleton className="h-4 w-1/3 max-w-xs" />
              <Skeleton className="h-4 w-1/2 max-w-xs" />
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
